import React, { useState, useEffect, useMemo } from "react";
import {
  CreditCard,
  CalendarDays,
  Hash,
  IndianRupee,
  Layers,
  CheckCircle2,
  Clock,
  XCircle,
  FileText,
  Eye,
  RefreshCw,
  ChevronDown,
} from "lucide-react";
import { BASE_URL } from "../../../config/api";
import RegularLoader from "../../../Component/Loader/RegularLoader";
import { Badge } from "../../../Component/RetailerMainComponents/Badge";
import Pagination from "../../../Component/Common/Pagination";
import SearchInput from "../../../Component/Common/SearchInput";
import DateRangePicker from "../../../Component/Common/DateRangePicker";
import RefreshButton from "../../../Component/Common/RefreshButton";
import { formatDateTime } from "../../../Component/Common/formatDateTime";
import { cn } from "./Invoice/invoiceUtils";
import axios from "axios";

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
function money(n) {
  if (n === null || n === undefined) return "—";
  return `₹${Number(n).toLocaleString()}`;
}

function parseDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isBetween(dateStr, from, to) {
  const d = parseDate(dateStr);
  if (!d) return true;
  const start = from ? parseDate(from) : null;
  const end = to ? parseDate(to) : null;
  if (start && d < start) return false;
  if (end) {
    const endDay = new Date(end);
    endDay.setHours(23, 59, 59, 999);
    if (d > endDay) return false;
  }
  return true;
}

function paymentStatusVariant(status) {
  const s = String(status || "").toLowerCase();
  if (s === "captured" || s === "paid" || s === "success") return "success";
  if (s === "authorized" || s === "pending") return "warning";
  if (s === "failed" || s === "refunded") return "danger";
  return "info";
}

function paymentStatusLabel(status) {
  const s = String(status || "").toLowerCase();
  if (s === "captured") return "Captured";
  if (s === "authorized") return "Authorized";
  if (s === "failed") return "Failed";
  if (s === "refunded") return "Refunded";
  if (s === "pending") return "Pending";
  if (s === "paid") return "Paid";
  if (s === "success") return "Success";
  return status || "—";
}

// ─────────────────────────────────────────────
// Invoice Chips  — shows latest 2 + overflow pill
// ─────────────────────────────────────────────
function InvoiceChips({ invoices = [], isRetailerMobile }) {
  if (!invoices || invoices.length === 0)
    return <span className="text-xs text-slate-400">N/A</span>;

  const visible = invoices.slice(0, 2);
  const overflow = invoices.length - 2;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((inv, i) => (
        <span
          key={i}
          className={cn(
            "inline-flex items-center rounded-lg px-2 py-0.5 text-[11px] font-semibold leading-tight",
            isRetailerMobile
              ? "bg-white/20 text-black border border-white/30 backdrop-blur-sm"
              : "bg-blue-50 text-blue-700 border border-blue-100"
          )}
        >
          {inv}
        </span>
      ))}
      {overflow > 0 && (
        <span
          className={cn(
            "inline-flex items-center rounded-lg px-2 py-0.5 text-[11px] font-semibold leading-tight",
            isRetailerMobile
              ? "bg-white/10 text-black border border-white/20"
              : "bg-slate-100 text-slate-600 border border-slate-200"
          )}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Mobile Card
// ─────────────────────────────────────────────
function PaymentMobileCard({ row, isRetailerMobile, onView }) {
  const cardCls = cn(
    "rounded-2xl border p-4 space-y-3 transition-all duration-300",
    isRetailerMobile
      ? "bg-white/10 backdrop-blur-2xl border-white/30 shadow-[0_10px_30px_rgba(31,38,135,0.2)]"
      : "bg-white border-blue-100 shadow-sm"
  );

  const labelCls = cn(
    "text-[10px] font-semibold uppercase tracking-wider",
    isRetailerMobile ? "text-black/50" : "text-slate-400"
  );
  const valueCls = cn(
    "text-sm font-semibold",
    isRetailerMobile ? "text-black" : "text-slate-900"
  );

  return (
    <div className={cardCls}>
      {/* Top Row */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className={labelCls}>Payment ID</p>
          <p className={cn(valueCls, "text-xs font-mono")}>{row.paymentId}</p>
        </div>
        <Badge variant={paymentStatusVariant(row.status)}>
          {paymentStatusLabel(row.status)}
        </Badge>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <p className={labelCls}>Amount</p>
          <p className={cn(valueCls, "text-base")}>{money(row.amount)}</p>
        </div>
        <div>
          <p className={labelCls}>Gateway</p>
          <p className={valueCls}>{row.gateway || "—"}</p>
        </div>
        <div>
          <p className={labelCls}>Service</p>
          <p className={valueCls}>{row.service || "—"}</p>
        </div>
        <div>
          <p className={labelCls}>Date</p>
          <p className={valueCls}>{formatDateTime(row.paymentDate)}</p>
        </div>
      </div>

      {/* Invoices */}
      <div>
        <p className={labelCls}>Invoice(s)</p>
        <div className="mt-1">
          <InvoiceChips invoices={row.invoiceNumbers} isRetailerMobile={isRetailerMobile} />
        </div>
      </div>

      {/* Action */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => onView(row)}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold transition",
            isRetailerMobile
              ? "bg-white/15 border border-white/30 text-black hover:bg-white/25 backdrop-blur-sm"
              : "bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100"
          )}
        >
          <Eye className="h-3.5 w-3.5" />
          View
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
export default function PaymentsTable({
  effectiveRole,
  targetId,
  isRetailerMobile = false,
  data
}) {
  console.log("data", data);
  const [payments, setPayments] = useState([]);
  console.log("payment data :", payments);

  const [loading, setLoading] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Modal
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  // Debounce
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, fromDate, toDate, pageSize]);

  // ── Fetch ──────────────────────────────────
  const fetchPayments = async () => {

    try {

      setLoading(true);

      const authUser = JSON.parse(
        localStorage.getItem("authUserData") || "{}"
      );

      const locationId =
        authUser?.location_id;

      const role =
        Number(authUser?.role);

      // retailer contact id
      const contactId =
        data?.zoho_id ||
        data?.contact_id ||
        authUser?.zoho_id;

      if (!locationId) {

        setPayments([]);

        return;
      }

      let apiUrl = "";

      // ✅ ADMIN → only location_id
      if (role === 1) {
        // Admin viewing retailer details
        if (targetId && contactId) {
          apiUrl =
            `${BASE_URL}zoho/get-payments?location_id=${locationId}&contact_id=${contactId}`;
        } else {
          // Admin own payments page
          apiUrl =
            `${BASE_URL}zoho/get-payments?location_id=${locationId}`;
        }
      }

      // ✅ RETAILER → location_id + contact_id
      else if (role === 5) {

        if (!contactId) {

          setPayments([]);

          return;
        }

        apiUrl =
          `${BASE_URL}zoho/get-payments?location_id=${locationId}&contact_id=${contactId}`;
      }

      // ✅ OTHER ROLES
      else {

        if (!contactId) {

          setPayments([]);

          return;
        }

        apiUrl =
          `${BASE_URL}zoho/get-payments?location_id=${locationId}&contact_id=${contactId}`;
      }

      const response =
        await axios.get(
          apiUrl,
          {
            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );
      
      const json =
        response.data;
        

      const rows =
        (json?.data || []).map(
          (p, index) => {

            const z =
              p.z_json || {};

            return {

              id:
                p.id || index,

              paymentId:
                z.payment_number ||
                p.payment_number ||
                p.payment_id ||
                `PAY-${index + 1}`,

              paymentDate:
                z.created_time ||
                z.date ||
                p.date,

              gateway:
                z.payment_mode ||
                p.payment_mode ||
                "Zoho",

              amount:
                Number(
                  z.amount ||
                    p.amount ||
                    0
                ),

              service:
                z.location_name ||
                p.location_name ||
                "Wallet Payment",

              status:
                z.payment_status ||
                "paid",

              invoiceNumbers:
                Array.isArray(
                  z.applied_invoices
                )
                  ? z.applied_invoices.map(
                      (inv) =>
                        inv.invoice_number ||
                        inv.invoice_id ||
                        "—"
                    )
                  : [],

              orderId:
                z.reference_number ||
                p.reference_number ||
                "—",

              signature:
                "—",

              notes:
                z.description ||
                p.description ||
                null,

              customerName:
                z.customer_name ||
                p.customer_name ||
                "—",

              customerCode:
                z.customer_id ||
                p.contact_id ||
                "—",

              phone:
                "—",

              unused_amount:
                z.unused_amount,
            };
          }
        );

      setPayments(rows);
      
    } catch (err) {

      console.error(
        "❌ Payments fetch failed",
        err
      );

    } finally {

      setLoading(false);

    }
  };

  useEffect(() => {
    fetchPayments();
  }, [effectiveRole, targetId, debouncedSearch, fromDate, toDate]);

  // ── Filter ────────────────────────────────
  const filteredRows = useMemo(() => {
    let rows = [...payments];
    const q = debouncedSearch.trim().toLowerCase();

    if (statusFilter !== "all") {
      rows = rows.filter(
        (r) => paymentStatusLabel(r.status).toLowerCase() === statusFilter.toLowerCase()
      );
    }

    rows = rows.filter((r) => isBetween(r.paymentDate, fromDate, toDate));

    if (q) {
      rows = rows.filter((r) =>
        [r.paymentId, r.gateway, r.service, r.customerName, r.customerCode, r.phone, ...(r.invoiceNumbers || [])]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }

    return rows;
  }, [payments, debouncedSearch, statusFilter, fromDate, toDate]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  // ── Styles ────────────────────────────────
  const containerCls = cn(
    "rounded-2xl border transition-all duration-300",
    isRetailerMobile
      ? "bg-white/10 backdrop-blur-2xl border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)]"
      : "bg-white border-blue-100"
  );

  const inputCls = cn(
    "h-9 rounded-xl text-xs font-semibold outline-none transition-all duration-300 px-2",
    isRetailerMobile
      ? "bg-white/10 backdrop-blur-xl border border-white/30 text-slate-800 shadow-[0_10px_25px_rgba(31,38,135,0.15)]"
      : "border border-blue-100 bg-white"
  );

  const thCls = cn(
    "px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap",
    isRetailerMobile ? "text-black/60" : "text-slate-500"
  );

  const tdCls = cn(
    "px-4 py-3 text-sm",
    isRetailerMobile ? "text-black" : "text-slate-800"
  );

  const trCls = cn(
    "border-b transition-colors",
    isRetailerMobile
      ? "border-white/10 hover:bg-white/10"
      : "border-slate-50 hover:bg-blue-50/40"
  );

  // ── Modal ─────────────────────────────────
  function PaymentModal() {
    if (!openModal || !selectedPayment) return null;
    const p = selectedPayment;

    const fields = [
      ["Payment ID", p.paymentId],
      ["Order ID", p.orderId],
      ["Gateway", p.gateway],
      ["Amount", money(p.amount)],
      ["Service", p.service],
      ["Status", <Badge variant={paymentStatusVariant(p.status)}>{paymentStatusLabel(p.status)}</Badge>],
      ["Date", formatDateTime(p.paymentDate)],
      ["Customer", p.customerName],
      ["Customer Code", p.customerCode],
      ["Phone", p.phone],
    ];

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className={`absolute inset-0 ${isRetailerMobile ? "" : "bg-slate-900/40"}`}
          onClick={() => setOpenModal(false)}
        />
        <div
          className={cn(
            "relative w-full max-w-lg rounded-2xl shadow-xl",
            isRetailerMobile
              ? "bg-slate-500 backdrop-blur-2xl border border-white/30"
              : "bg-white border border-blue-100"
          )}
        >
          {/* Header */}
          <div className={cn("flex items-center justify-between p-4 border-b", isRetailerMobile ? "border-white/20" : "border-blue-100")}>
            <p className={cn("text-base font-semibold", isRetailerMobile ? "text-black" : "text-slate-900")}>
              Payment Details
            </p>
            <button
              onClick={() => setOpenModal(false)}
              className={cn(
                "rounded-xl p-2 transition",
                isRetailerMobile
                  ? "bg-white/10 border border-white/30 text-black hover:bg-white/20"
                  : "bg-blue-50 border border-blue-100 text-blue-700 hover:bg-blue-100"
              )}
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div className="p-4 max-h-[75vh] overflow-y-auto space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {fields.map(([label, value]) => (
                <div
                  key={label}
                  className={cn(
                    "rounded-xl p-3 border",
                    isRetailerMobile
                      ? "bg-white/10 border-white/20"
                      : "bg-slate-50 border-slate-100"
                  )}
                >
                  <p className={cn("text-[10px] font-semibold uppercase tracking-wider mb-0.5", isRetailerMobile ? "text-black/50" : "text-slate-400")}>
                    {label}
                  </p>
                  <div className={cn("text-sm font-semibold", isRetailerMobile ? "text-black" : "text-slate-900")}>
                    {value}
                  </div>
                </div>
              ))}
            </div>

            {/* Invoices */}
            {p.invoiceNumbers?.length > 0 && (
              <div className={cn("rounded-xl p-3 border", isRetailerMobile ? "bg-white/10 border-white/20" : "bg-slate-50 border-slate-100")}>
                <p className={cn("text-[10px] font-semibold uppercase tracking-wider mb-2", isRetailerMobile ? "text-black/50" : "text-slate-400")}>
                  Invoice(s)
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {p.invoiceNumbers.map((inv, i) => (
                    <span
                      key={i}
                      className={cn(
                        "rounded-lg px-2 py-1 text-xs font-semibold",
                        isRetailerMobile
                          ? "bg-white/20 border border-white/30 text-black"
                          : "bg-blue-50 border border-blue-100 text-blue-700"
                      )}
                    >
                      {inv}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────
  return (
    <>
      <PaymentModal />

      {/* ── Toolbar ─────────────────────────── */}
      <div className={cn("rounded-2xl p-3 mb-3 space-y-3 transition-all duration-300", isRetailerMobile ? "bg-white/10 backdrop-blur-2xl border border-white/30" : "bg-white border border-blue-100")}>
        <SearchInput
          value={search}
          onChange={setSearch}
          onClear={() => setSearch("")}
          placeholder="Search payment ID / customer / invoice..."
          size="md"
        />

        <div className="flex flex-wrap gap-2 items-center">
          {/* Date range */}
          <div className="flex-1 min-w-[160px]">
            <DateRangePicker
              startDate={fromDate ? new Date(fromDate) : null}
              endDate={toDate ? new Date(toDate) : null}
              onChange={(start, end) => {
                setFromDate(start ? start.toISOString().slice(0, 10) : "");
                setToDate(end ? end.toISOString().slice(0, 10) : "");
              }}
              isRetailerMobile={isRetailerMobile}
            />
          </div>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={cn(inputCls, "w-28")}
          >
            <option value="all">All Status</option>
            <option value="Captured">Captured</option>
            <option value="Authorized">Authorized</option>
            <option value="Pending">Pending</option>
            <option value="Failed">Failed</option>
            <option value="Refunded">Refunded</option>
          </select>

          {/* Page size */}
          {/* <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className={cn(inputCls, "w-20")}
          >
            {[5, 10, 20, 50].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select> */}

          {/* Actions */}
          <button
            type="button"
            onClick={() => {
              setSearch("");
              setStatusFilter("all");
              setFromDate("");
              setToDate("");
              setCurrentPage(1);
            }}
            className={cn(
              "h-9 rounded-xl px-3 text-xs font-semibold transition",
              isRetailerMobile
                ? "bg-white/10 border border-white/30 text-slate-800 hover:bg-white/20"
                : "border border-blue-200 bg-white text-blue-700 hover:bg-blue-50"
            )}
          >
            Reset
          </button>

          <RefreshButton onClick={fetchPayments} />
        </div>
      </div>

      {/* ── Desktop Table ────────────────────── */}
      <div className="hidden lg:block">
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <RegularLoader />
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <CreditCard className="h-10 w-10 text-slate-300 mb-3" />
            <p className="text-sm font-semibold text-slate-500">No payments found</p>
          </div>
        ) : (
          <div className={containerCls}>
            <div className="overflow-x-auto rounded-2xl">
              <table className="w-full">
                <thead>
                  <tr className={cn("border-b", isRetailerMobile ? "border-white/15" : "border-slate-100")}>
                    <th className={thCls}>
                      <span className="flex items-center gap-1.5">
                        <Hash className="h-3.5 w-3.5" /> Payment ID
                      </span>
                    </th>
                    <th className={thCls}>
                      <span className="flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5" /> Date
                      </span>
                    </th>
                    <th className={thCls}>
                      <span className="flex items-center gap-1.5">
                        <CreditCard className="h-3.5 w-3.5" /> Gateway
                      </span>
                    </th>
                    <th className={thCls}>
                      <span className="flex items-center gap-1.5">
                        <IndianRupee className="h-3.5 w-3.5" /> Amount
                      </span>
                    </th>
                    <th className={thCls}>
                      <span className="flex items-center gap-1.5">
                        <Layers className="h-3.5 w-3.5" /> Service
                      </span>
                    </th>
                    {/* <th className={thCls}>Status</th> */}
                    <th className={thCls}>
                      <span className="flex items-center gap-1.5">
                        <FileText className="h-3.5 w-3.5" /> Invoice No.
                      </span>
                    </th>
                    <th className={cn(thCls, "text-center")}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row) => (
                    <tr key={row.id} className={trCls}>
                      {/* Payment ID */}
                      <td className={tdCls}>
                        <span className="font-mono text-xs font-semibold">{row.paymentId}</span>
                      </td>

                      {/* Date */}
                      <td className={tdCls}>
                        <span className="text-xs">{formatDateTime(row.paymentDate)}</span>
                      </td>

                      {/* Gateway */}
                      <td className={tdCls}>
                        <span
                          className={cn(
                            "inline-flex items-center rounded-lg px-2 py-0.5 text-xs font-semibold",
                            isRetailerMobile
                              ? "bg-white/15 border border-white/25 text-black"
                              : "bg-slate-50 border border-slate-200 text-slate-700"
                          )}
                        >
                          {row.gateway}
                        </span>
                      </td>

                      {/* Amount */}
                      <td className={tdCls}>
                        <span className="font-semibold">{money(row.amount)}</span>
                      </td>

                      {/* Service */}
                      <td className={tdCls}>
                        <span className="text-xs">{row.service}</span>
                      </td>

                      {/* Status */}
                      {/* <td className={tdCls}>
                        <Badge variant={paymentStatusVariant(row.status)}>
                          {paymentStatusLabel(row.status)}
                        </Badge>
                      </td> */}

                      {/* Invoice Numbers */}
                      <td className={tdCls}>
                        <InvoiceChips invoices={row.invoiceNumbers} isRetailerMobile={isRetailerMobile} />
                      </td>

                      {/* Action */}
                      <td className={cn(tdCls, "text-center")}>
                        <span className="text-xs">{row.unused_amount}</span>
                        {/* <button
                          type="button"
                          onClick={() => {
                            setSelectedPayment(row);
                            setOpenModal(true);
                          }}
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold transition",
                            isRetailerMobile
                              ? "bg-white/15 border border-white/30 text-black hover:bg-white/25"
                              : "bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100"
                          )}
                        >
                          <Eye className="h-3.5 w-3.5" />
                          View
                        </button> */}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={cn("px-4 py-3 border-t", isRetailerMobile ? "border-white/15" : "border-slate-100")}>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Mobile Cards ─────────────────────── */}
      <div className="grid grid-cols-1 gap-3 lg:hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <RegularLoader />
          </div>
        ) : pageRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <CreditCard className="h-10 w-10 text-slate-300 mb-3" />
            <p className="text-sm font-semibold text-slate-500">No payments found</p>
          </div>
        ) : (
          <>
            {pageRows.map((row) => (
              <PaymentMobileCard
                key={row.id}
                row={row}
                isRetailerMobile={isRetailerMobile}
                onView={(r) => {
                  setSelectedPayment(r);
                  setOpenModal(true);
                }}
              />
            ))}

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}